import axios from 'axios';
import { baseUrl, getCurrentUser } from '../../../util/host';

export const getApiBase = () => baseUrl('REACT_APP_API_URL');

/** Standard auth + content headers matching the sellerLocation pattern */
export const slaHeaders = (extras = {}) => {
  const token = getCurrentUser()?.token || '';
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...extras,
  };
};

/**
 * Postman-style header documenting the logical route (placeholders, not real ids).
 * Matches collection `non-dynamic-url` entries for logging / gateway hints.
 */
export const NON_DYNAMIC_URL_HEADER = 'non-dynamic-url';

export const nonDynamicUrlHeaders = (pattern) => ({
  [NON_DYNAMIC_URL_HEADER]: pattern,
});

/** Static path patterns aligned with the ADR SMPR Postman collection */
export const SLA_PATH_PATTERN = {
  RULES: '/api/sla/rules',
  RULES_BY_ID: '/api/sla/rules/:id',
  RULES_ACTIVATE: '/api/sla/rules/activate/:id',
  OPTION_OVERRIDES: '/api/sla/option-overrides',
  /** GET list — Postman `non-dynamic-url` (filters are sent as query params) */
  OPTION_OVERRIDES_LIST: '/api/sla/option-overrides/:status/:last_mile_warehouse',
  OPTION_OVERRIDES_BY_ID: '/api/sla/option-overrides/:id',
  OPTION_OVERRIDES_UPLOAD: '/api/sla/option-overrides/upload',
  SEARCH_CITY: '/api/sla/search-city',
  SEARCH_CITY_DEBUG: '/api/sla/search-city/debug',
  ORDER_SHIPMENT_SLA: '/api/sla/order/shipment-sla',
  ORDER_EXPRESS_FLAG: '/api/sla/order/express-flag',
  ADDRESS_FAST_DELIVERY_EXCLUSIONS: '/api/address/fast-delivery/exclusions',
};

/** Use for URL path segments (Mongo _id, etc.); returns empty string if missing */
export const resourceIdSegment = (id) => {
  if (id == null || id === '') return '';
  const s = String(id).trim();
  return s ? encodeURIComponent(s) : '';
};

/**
 * Backend envelope: { status, statusCode, statusMsg, data }
 * Throws with statusMsg when status === false on a 200 response.
 */
export const assertOkEnvelope = (res) => {
  const body = res?.data;
  if (body && body.status === false) {
    const err = new Error(body.statusMsg || 'Request failed');
    err.response = { data: body };
    throw err;
  }
  return body;
};

export const buildSlaRulesQuery = ({ statusFilter, warehouseFilter }) => {
  const params = new URLSearchParams();
  if (statusFilter === 'true' || statusFilter === 'false') {
    params.set('status', statusFilter);
  }
  if (warehouseFilter) {
    params.set('last_mile_warehouse', warehouseFilter);
  }
  const q = params.toString();
  return q ? `?${q}` : '';
};

export const buildOptionOverridesQuery = ({
  statusFilter,
  warehouseFilter,
  optionIdFilter,
  destinationCountryFilter,
}) => {
  const params = new URLSearchParams();
  if (statusFilter === 'true' || statusFilter === 'false') {
    params.set('status', statusFilter);
  }
  if (warehouseFilter) {
    params.set('last_mile_warehouse', warehouseFilter);
  }
  if (optionIdFilter && String(optionIdFilter).trim()) {
    params.set('option_id', String(optionIdFilter).trim());
  }
  if (destinationCountryFilter && String(destinationCountryFilter).trim()) {
    params.set('destination_country', String(destinationCountryFilter).trim());
  }
  const q = params.toString();
  return q ? `?${q}` : '';
};

/**
 * Fetch provinces for a country (mirrors loadProvince, but local/no-Redux and
 * additionally forwards `warehouse` so the backend can scope the list).
 * Returns [] on error so parallel fetches never reject the whole batch.
 */
export const fetchProvincesForCountry = async ({ country, warehouse }) => {
  if (!country) return [];
  try {
    const res = await axios.post(
      `${getApiBase()}/api/address/find/province`,
      { country, warehouse: warehouse || undefined },
      { headers: slaHeaders() }
    );
    const list = res?.data?.response;
    return Array.isArray(list) ? list : [];
  } catch (err) {
    console.error('fetchProvincesForCountry failed', { country, warehouse, err });
    return [];
  }
};

/**
 * Fetch cities for a {country, provinceId} pair (mirrors loadCity with `warehouse`).
 * Returns [] on error.
 */
export const fetchCitiesForProvince = async ({ country, provinceId, warehouse }) => {
  if (!country || provinceId == null || provinceId === '') return [];
  try {
    const res = await axios.post(
      `${getApiBase()}/api/address/find/city`,
      { country, provinceId, warehouse: warehouse || undefined },
      { headers: slaHeaders() }
    );
    const list = res?.data?.response;
    return Array.isArray(list) ? list : [];
  } catch (err) {
    console.error('fetchCitiesForProvince failed', { country, provinceId, warehouse, err });
    return [];
  }
};
